// /brokers/investigate — broker investigations (SM-716, SM-621 phase 08a;
// D63).
//
// Start from one subject — a user, a broker account, an order, a trade
// or a payout batch — and read the trail backoffice-service composes
// from the services that own each hop: broker accounts → orders → fills
// → fees → earnings → funding transfers → payout batches, ordered by
// time, then that chain position, then id (orderTraceSteps). Each hop
// links into the console page that owns it where one exists.
//
// A trace is never blank because one service is down: a source that
// could not be read comes back as a GAP and is listed above the table,
// and a per-kind bound that cut the trail sets `truncated`. Read-only;
// BROKER_INVESTIGATE. The subject lives in the URL so a trace is
// shareable.
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import {
  accountStatusLabel,
  accountStatusTone,
  balanceModeLabel,
  gapSentence,
  orderTraceSteps,
  stepKindLabel,
  stepKindTone,
  subjectKindLabel,
  subjectPlaceholder,
  traceTarget,
} from "@/components/brokers/governance";
import { canonToWire, msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokerInvestigation } from "@/lib/broker-queries";
import {
  INVESTIGATION_SUBJECT_KINDS,
  type InvestigationStep,
  type InvestigationSubjectKind,
  type InvestigationTrace,
} from "@/lib/broker-types";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

interface Search {
  kind?: InvestigationSubjectKind;
  id?: string;
}

export const Route = createFileRoute("/_app/brokers/investigate")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    kind: INVESTIGATION_SUBJECT_KINDS.find((k) => k === s.kind),
    id: typeof s.id === "string" && s.id.trim() ? s.id.trim() : undefined,
  }),
  component: BrokerInvestigatePage,
});

const th = "px-3 py-2.5 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-2.5 text-xs";

function BrokerInvestigatePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canInvestigate = perms.has(Perm.BrokerInvestigate);
  const kind: InvestigationSubjectKind = search.kind ?? "USER";
  const [draftKind, setDraftKind] = useState<InvestigationSubjectKind>(kind);
  const [draftID, setDraftID] = useState(search.id ?? "");
  useEffect(() => {
    setDraftKind(kind);
    setDraftID(search.id ?? "");
  }, [kind, search.id]);

  const q = useQuery({
    queryKey: brokerKeys.investigation(kind, search.id ?? ""),
    queryFn: () => fetchBrokerInvestigation(kind, search.id ?? ""),
    enabled: canInvestigate && Boolean(search.id),
    retry: false,
  });

  if (perms.loaded && !canInvestigate) {
    return (
      <EmptyState title="No access" description="You need the BROKER_INVESTIGATE permission to trace broker activity." />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Investigations</h1>
        <p className="text-sm text-muted">
          Trace one user, broker account, order, trade or payout batch through its orders, fills, fees, earnings,
          funding transfers and payouts. Each hop comes from the service that owns it; a source that cannot be read is
          listed rather than left out silently.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          const id = draftID.trim();
          void navigate({ search: { kind: draftKind, id: id || undefined } });
        }}
      >
        <div className="w-52">
          <Select
            label="Start from"
            value={draftKind}
            onChange={(e) => setDraftKind(e.target.value as InvestigationSubjectKind)}
            options={INVESTIGATION_SUBJECT_KINDS.map((k) => ({ value: k, label: subjectKindLabel(k) }))}
          />
        </div>
        <div className="w-80">
          <Input
            label="ID"
            placeholder={subjectPlaceholder(draftKind)}
            value={draftID}
            onChange={(e) => setDraftID(e.target.value)}
            className="font-mono"
          />
        </div>
        <Button type="submit" disabled={draftID.trim() === ""} loading={q.isFetching}>
          Trace
        </Button>
      </form>

      {!search.id ? (
        <p className="text-xs text-muted">Enter an ID to start a trace.</p>
      ) : null}
      {q.isLoading ? <Skeleton className="h-40" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
      {q.data ? <TraceView trace={q.data} /> : null}
    </div>
  );
}

function TraceView({ trace }: { trace: InvestigationTrace }) {
  const steps = orderTraceSteps(trace.steps);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-border bg-surface-2 px-4 py-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted">Subject</span>
          <Badge tone="muted">{subjectKindLabel(trace.subject_kind)}</Badge>
          <span className="font-mono text-2xs">{trace.subject_id}</span>
        </div>
        {trace.user_id ? (
          <div className="flex items-center gap-2">
            <span className="text-muted">User</span>
            <Link to="/users/$id" params={{ id: trace.user_id }} className="font-mono text-2xs hover:underline">
              {trace.user_id}
            </Link>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <span className="text-muted">Composed</span>
          <span className="text-2xs">{fmtDate(msToIso(trace.generated_at_unix_ms))}</span>
        </div>
      </div>

      {trace.gaps.length > 0 ? (
        <div role="alert" className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-xs text-warning">
          <p className="font-semibold">Part of this trace could not be read. The hops below are real, but these sources are missing from it:</p>
          <ul className="mt-2 space-y-2">
            {trace.gaps.map((g, i) => (
              <li key={`${g.source}-${g.code}-${i}`} className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold">{g.source}</span>
                  <span className="font-mono text-2xs">{g.code}</span>
                </div>
                <p className="text-t-1">{gapSentence(g.code)}</p>
                {g.message ? <p className="font-mono text-2xs text-t-2">{g.message}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {trace.truncated ? (
        <p className="rounded-md border border-border bg-surface-2 px-4 py-2 text-2xs text-t-2">
          This trace was cut at the per-kind limit, so older hops are missing. Start from a narrower subject — an order
          or a payout batch — to see the rest.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Broker accounts</CardTitle>
          <span className="text-2xs text-muted">{trace.accounts.length}</span>
        </CardHeader>
        <CardBody className="p-0">
          {trace.accounts.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted">No broker account was found for this subject.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Account</th>
                    <th className={th}>Serviced by</th>
                    <th className={th}>Balance mode</th>
                    <th className={th}>Status</th>
                    <th className={th}>Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {trace.accounts.map((a) => (
                    <tr key={a.broker_account_id} className="border-b border-border/60 last:border-0">
                      <td className={`${td} px-5 font-mono text-2xs`}>{a.broker_account_id}</td>
                      <td className={td}>
                        <Link to="/brokers/$id" params={{ id: a.broker_id }} className="font-mono text-2xs hover:underline">
                          {a.broker_id}
                        </Link>
                      </td>
                      <td className={td}>{balanceModeLabel(a.balance_mode)}</td>
                      <td className={td}>
                        <Badge tone={accountStatusTone(a.status)}>{accountStatusLabel(a.status)}</Badge>
                      </td>
                      <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(a.created_at_unix_ms))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trail</CardTitle>
          <span className="text-2xs text-muted">{steps.length}</span>
        </CardHeader>
        <CardBody className="p-0">
          {steps.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-muted">Nothing was found for this subject.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>When</th>
                    <th className={th}>Kind</th>
                    <th className={th}>ID</th>
                    <th className={th}>Broker</th>
                    <th className={`${th} text-right`}>Amount</th>
                    <th className={th}>Status</th>
                    <th className={th}>Source</th>
                    <th className={th}>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((s, i) => (
                    <StepRow key={`${s.kind}-${s.id}-${i}`} s={s} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StepRow({ s }: { s: InvestigationStep }) {
  const wire = s.amount ? canonToWire(s.amount) : undefined;
  return (
    <tr className="border-b border-border/60 align-top last:border-0">
      <td className={`${td} px-5 text-2xs text-muted`}>{fmtDate(msToIso(s.occurred_at_unix_ms))}</td>
      <td className={td}>
        <Badge tone={stepKindTone(s.kind)}>{stepKindLabel(s.kind)}</Badge>
      </td>
      <td className={`${td} font-mono text-2xs`}>
        <div className="text-t-1">{s.id}</div>
        {s.parent_id ? <div className="text-muted">↳ {s.parent_id}</div> : null}
        {s.broker_account_id ? <div className="text-muted">{s.broker_account_id}</div> : null}
        {s.summary ? <div className="mt-0.5 font-sans text-t-2">{s.summary}</div> : null}
        {s.reference ? <div className="text-muted">{s.reference}</div> : null}
      </td>
      <td className={td}>
        {s.broker_id ? (
          <Link to="/brokers/$id" params={{ id: s.broker_id }} className="font-mono text-2xs hover:underline">
            {s.broker_id}
          </Link>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className={`${td} text-right`}>
        {s.amount && s.currency ? (
          wire !== undefined ? (
            <CurrencyAmount currency={s.currency} value={wire} />
          ) : (
            <span className="font-mono text-2xs">
              {s.amount} {s.currency}
            </span>
          )
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className={`${td} font-mono text-2xs`}>{s.status || "—"}</td>
      <td className={`${td} font-mono text-2xs text-muted`}>{s.source}</td>
      <td className={td}>
        <StepLink s={s} />
      </td>
    </tr>
  );
}

const TAB_LINK = {
  funding: <>Broker funding</>,
  accounts: <>Broker accounts</>,
};

/** The console page a hop opens, when it has one. */
function StepLink({ s }: { s: InvestigationStep }) {
  const target = traceTarget(s);
  const cls = "text-2xs text-neon hover:underline";
  if (!target) return <span className="text-muted">—</span>;
  switch (target.page) {
    case "user":
      return (
        <Link to="/users/$id" params={{ id: target.userId }} className={cls}>
          User
        </Link>
      );
    case "orders":
      return (
        <Link to="/orders/history" search={{ user_id: target.userId }} className={cls}>
          Order history
        </Link>
      );
    case "trade":
      return (
        <Link to="/ops/trades/$tradeId" params={{ tradeId: target.tradeId }} className={cls}>
          Trade integrity
        </Link>
      );
    case "broker":
      return (
        <Link to="/brokers/$id" params={{ id: target.brokerId }} search={{ tab: target.tab }} className={cls}>
          {TAB_LINK[target.tab]}
        </Link>
      );
    case "payout":
      return (
        <Link to="/brokers/payouts" search={{ open: target.batchId }} className={cls}>
          Payout batch
        </Link>
      );
  }
}
